import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Badge,
  Button,
  IconButton,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../assets/styles/tokens";
import { Stack } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserData,
  selectUserToken,
} from "../../../../../../../features/userSlice";
import { TrendsDashboardEndPointLogicMobile } from "../../../../../../../logic/AssetManagement/TrendsMobile/TrendsDashboardEndPointLogicMobile";
import {
  TrendsDataSetQueryIds,
  TrendsFilterQueryIds,
} from "../../../helpers/funcs/trendsAllQueryIDs";
import { VariableSizeList, ListChildComponentProps } from "react-window";
import { useTrendsMobile } from "../../helpers/hooks/useTrendsMobile";
import {
  resetMobileFilters,
  setMobileFiltersPartial,
  setNotesLabelFilter,
  updateMobileFilter,
} from "../../../../../../../features/trendsSlice";
import { PARAM, removeTrendParams } from "../../helpers/funcs/filterURLRemove";
import CloseIcon from "@mui/icons-material/Close";
import { useTrackOpen } from "../../helpers/hooks/useTrackOpen";
const truncate = (text: string, limit = 18) => {
  if (!text) return "";
  return text.length > limit ? text.slice(0, limit) + "..." : text;
};
const Puller = styled("div")(({ theme }) => ({
  width: spacing.Spacing["4xl"].$value,
  height: spacing.Spacing.xs.$value,
  backgroundColor: colors.GrayLightMode[300].$value,
  borderRadius: spacing.Radius.xs.$value,
  position: "absolute",
  top: spacing.Spacing.xl.$value,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: colors.GrayLightMode[300].$value,
  }),
}));

const LISTBOX_PADDING = 8;
const ITEM_SIZE = 45;

const renderRow = ({ data, index, style }: ListChildComponentProps) => {
  const dataSet = data[index];
  return React.cloneElement(dataSet, {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});
OuterElementType.displayName = "OuterElementType";

function useResetCache(data: number) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

export const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: React.ReactElement[] = React.Children.toArray(
    children,
  ) as React.ReactElement[];
  const itemCount = itemData.length;

  const getHeight = () => {
    if (itemCount > 8) return 8 * ITEM_SIZE;
    return itemCount * ITEM_SIZE;
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={() => ITEM_SIZE}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const sharedAutocompleteSx = {
  minWidth: 184,
  maxWidth: 184,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    height: spacing.Spacing["4xl"].$value,
    py: "0 !important",
    fontSize: typography.Size.Text.sm.$value,
    fontWeight: typography.Font_weight.Semibold.$value,
    color: colors.GrayLightMode[700].$value,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.GrayLightMode[300].$value,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.Brand[600].$value,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.Brand[600].$value,
    },
    "&.Mui-disabled": {
      background: "#E9EAEB",
      color: colors.GrayLightMode[500].$value,
    },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.GrayLightMode[300].$value,
    },
    "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": {
      color: colors.GrayLightMode[700].$value,
    },
  },
  "& .MuiAutocomplete-input": {
    padding: "7px !important",
    fontSize: typography.Size.Text.sm.$value,
  },
};

interface Option {
  label: string;
  value: string;
}

interface VirtualizedAutocompleteProps {
  options: Option[];
  value: Option | null;
  onChange: (val: Option | null) => void;
  placeholder: string;
  disabled?: boolean;
  loading?: boolean;
  onOpen?: () => void;
}

const VirtualizedAutocomplete: React.FC<VirtualizedAutocompleteProps> = ({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  loading = false,
  onOpen,
}) => (
  <Autocomplete
    disableListWrap
    ListboxComponent={ListboxComponent}
    options={options}
    value={value}
    onChange={(_, newValue) => onChange(newValue)}
    onOpen={onOpen}
    disabled={disabled}
    loading={loading}
    isOptionEqualToValue={(opt, val) => opt.value === val.value}
    getOptionLabel={(opt) => opt.label}
    sx={sharedAutocompleteSx}
    renderInput={(params) => (
      <TextField
        {...params}
        placeholder={placeholder}
        size="small"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={14} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
        sx={{
          "& .MuiInputBase-input::placeholder": {
            fontSize: typography.Size.Text.sm.$value,
            color: colors.GrayLightMode[700].$value,
            opacity: 1,
          },
        }}
      />
    )}
    renderOption={(props, option) => (
      <li {...props} key={option.value}>
        {truncate(option.label)}
      </li>
    )}
    slotProps={{
      paper: {
        sx: {
          "& .MuiAutocomplete-listbox": {
            padding: 0,
          },
          "& .MuiMenuItem-root, & li": {
            fontSize: typography.Size.Text.sm.$value,
            minHeight: spacing.Spacing["5xl"].$value,
          },
        },
      },
    }}
  />
);

export default function TrendsMobileFilterBy() {
  const filters = useSelector((state: any) => state.trends.mobileFilters);
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const [filterLoading, setFilterLoading] = React.useState(false);
  const didRestoreRef = React.useRef(false);
  const pendingAutoFetchRef = React.useRef<any>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const response = useSelector(selectUserData);
  const websiteSharedUids = searchParams.get("workspace");
  const startDate = searchParams.get("startDateTM");
  const endDate = searchParams.get("endDateTM");
  const dateType = searchParams.get("dateType");
  const { fetchTrends } = useTrendsMobile({
    autoFetch: false,
  });
  const loadingTrendsMobData = useSelector(
    (state: any) => state.trends?.loadingTrendsMobData,
  );
  const filterDataByIdMobileMap = useSelector(
    (state: any) => state.trends?.filterDataByIdMobileMap,
  );
  const trafficSourceDataMobile = useSelector(
    (state: any) => state.trends?.trafficSourceDataMobile,
  );
  const geoDataMobile = useSelector(
    (state: any) => state.trends?.geoDataMobile,
  );
  const activityNotesDataTrendsMobile = useSelector(
    (state: any) => state.trends?.activityNotesDataTrendsMobile,
  );
  const mobileFilters = useSelector(
    (state: any) => state.trends?.mobileFilters,
  );
  const notesLabelFilter = useSelector(
    (state: any) => state.trends.notesLabelFilter,
  );
  const trackOpen = useTrackOpen();

  const availableLabels = React.useMemo(() => {
    if (
      !activityNotesDataTrendsMobile ||
      !Array.isArray(activityNotesDataTrendsMobile)
    )
      return [];
    const labelSet = new Set<string>();
    activityNotesDataTrendsMobile.forEach((note: any) => {
      if (Array.isArray(note?.labels)) {
        note.labels.forEach((label: string) => labelSet.add(label));
      }
    });
    return Array.from(labelSet).sort();
  }, [activityNotesDataTrendsMobile]);

  const isValidValue = (val: any) =>
    val !== null && val !== undefined && val !== "" && val !== "null";
  const trafficSourceOptions: Option[] = React.useMemo(
    () =>
      Array.isArray(trafficSourceDataMobile)
        ? trafficSourceDataMobile
            .filter((item) => isValidValue(item?.traffic_source_name))
            .map((item: any) => ({
              label: item.traffic_source_name,
              value: item.traffic_source_name,
            }))
        : [],
    [trafficSourceDataMobile],
  );
  const campaignData = React.useMemo(
    () => filterDataByIdMobileMap?.campaign?.query_output || [],
    [filterDataByIdMobileMap],
  );

  const adGroupData = React.useMemo(
    () => filterDataByIdMobileMap?.adgroup?.query_output || [],
    [filterDataByIdMobileMap],
  );

  const keywordData = React.useMemo(
    () => filterDataByIdMobileMap?.keyword?.query_output || [],
    [filterDataByIdMobileMap],
  );

  const isAllFiltersReady =
    Array.isArray(campaignData) &&
    campaignData.length > 0 &&
    Array.isArray(adGroupData) &&
    adGroupData.length > 0 &&
    Array.isArray(keywordData) &&
    keywordData.length > 0;

  const countryOptions: Option[] = React.useMemo(
    () =>
      Array.isArray(geoDataMobile)
        ? geoDataMobile
            .filter((item) => isValidValue(item?.name))
            .map((item: any) => ({
              label: item.name,
              value: item.name ?? item.name,
            }))
        : [],
    [geoDataMobile],
  );

  const campaignOptions: Option[] = React.useMemo(
    () =>
      Array.isArray(campaignData)
        ? campaignData
            .filter(
              (item) =>
                isValidValue(item?.camp_id) && isValidValue(item?.camp_name),
            )
            .map((item: any) => ({
              label: item.camp_name,
              value: String(item.camp_id),
            }))
        : [],
    [campaignData],
  );

  const adGroupOptions: Option[] = React.useMemo(
    () =>
      Array.isArray(adGroupData)
        ? adGroupData
            .filter(
              (item) =>
                isValidValue(item?.ag_id) && isValidValue(item?.ag_name),
            )
            .map((item: any) => ({
              label: item.ag_name,
              value: String(item.ag_id),
            }))
        : [],
    [adGroupData],
  );

  const keywordOptions: Option[] = React.useMemo(
    () =>
      Array.isArray(keywordData)
        ? keywordData
            .filter(
              (item) =>
                isValidValue(item?.kw_id) && isValidValue(item?.kw_name),
            )
            .map((item: any) => ({
              label: item.kw_name,
              value: String(item.kw_id),
            }))
        : [],
    [keywordData],
  );

  const selectedDimensions = React.useMemo(() => {
    const uidList = websiteSharedUids?.split(",") ?? [];
    return (
      response?.data?.website_permissions?.flatMap((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0];
        if (!uidList.includes(shared_uid)) return [];
        return (siteObj[shared_uid].dimensions ?? []).map(
          (dim: any) => dim.dimension_name,
        );
      }) ?? []
    );
  }, [response, websiteSharedUids]);

  const dataSet = searchParams.get("dateType");
  const toggleDrawer = (newOpen: boolean) => () => setOpen(newOpen);
  const isApplyDisabled = !filters.trafficSource;
  const handleClearAll = () => {
    dispatch(resetMobileFilters());
    dispatch(setNotesLabelFilter(null));
    setSearchParams((prev) => removeTrendParams(prev));
    didRestoreRef.current = false;
    pendingAutoFetchRef.current = null;
    toggleDrawer(false)();
    fetchTrends({
      oauthTokenHeader,
      queryId: TrendsDataSetQueryIds.CLICK,
      startDate: startDate || "",
      endDate: endDate || "",
      timeUnit: "day",
      type: "site",
      matchtype: "all",
      websiteSharedUids: websiteSharedUids ? websiteSharedUids.split(",") : [],
      dimensions: selectedDimensions,
      trafficSource: "all",
      country: "all",
      campaignId: "1",
      adgroupId: "1",
      kwId: "1",
    });
  };

  const fetchDependentFilters = React.useCallback(
    async (trafficSourceValue: string) => {
      const commonPayload = {
        oauthTokenHeader,
        websiteSharedUids: websiteSharedUids
          ? websiteSharedUids.split(",")
          : [],
        dimensions: selectedDimensions,
        trafficSource: trafficSourceValue,
      };
      setFilterLoading(true);
      try {
        await Promise.all([
          TrendsDashboardEndPointLogicMobile.getFilterOptionsDataForCampaignAdgroupKeyword(
            {
              queryId: TrendsFilterQueryIds.CAMP,
              key: "campaign",
              type: "site",
              account_shared_uid: 1,
              campaign_status: "all",
              ...commonPayload,
            },
          ),
          TrendsDashboardEndPointLogicMobile.getFilterOptionsDataForCampaignAdgroupKeyword(
            {
              queryId: TrendsFilterQueryIds.ADGROUP,
              key: "adgroup",
              type: "site",
              ...commonPayload,
            },
          ),
          TrendsDashboardEndPointLogicMobile.getFilterOptionsDataForCampaignAdgroupKeyword(
            {
              queryId: TrendsFilterQueryIds.KEYWORD,
              key: "keyword",
              type: "site",
              ...commonPayload,
            },
          ),
        ]);
      } catch (error) {
        console.error(error);
      } finally {
        setFilterLoading(false);
      }
    },
    [oauthTokenHeader, websiteSharedUids, selectedDimensions],
  );

  React.useEffect(() => {
    if (didRestoreRef.current) return;
    const urlTrafficSource = searchParams.get(PARAM.TRAFFIC_SOURCE);
    if (!urlTrafficSource) return;
    if (trafficSourceOptions.length === 0) return;
    didRestoreRef.current = true;
    const tsOption = trafficSourceOptions.find(
      (o) => o.value === urlTrafficSource,
    ) ?? {
      label: urlTrafficSource,
      value: urlTrafficSource,
    };
    const urlCountry = searchParams.get(PARAM.COUNTRY);
    const countryOption = urlCountry
      ? (countryOptions.find((o) => o.value === urlCountry) ?? {
          label: urlCountry,
          value: urlCountry,
        })
      : null;
    dispatch(
      setMobileFiltersPartial({
        trafficSource: tsOption,
        country: countryOption,
      }),
    );
    pendingAutoFetchRef.current = {
      oauthTokenHeader,
      queryId: TrendsDataSetQueryIds.CLICK,
      startDate: startDate || "",
      endDate: endDate || "",
      timeUnit: "day",
      type: "site",
      matchtype: "all",
      websiteSharedUids: websiteSharedUids ? websiteSharedUids.split(",") : [],
      dimensions: selectedDimensions,
      trafficSource: urlTrafficSource,
      country: urlCountry || "all",
      campaignId: searchParams.get(PARAM.CAMPAIGN_ID) || "1",
      adgroupId: searchParams.get(PARAM.ADGROUP_ID) || "1",
      kwId: searchParams.get(PARAM.KEYWORD_ID) || "1",
    };
    fetchDependentFilters(urlTrafficSource);
    // eslint-disable-next-line
  }, [trafficSourceOptions]);

  React.useEffect(() => {
    if (!isAllFiltersReady) return;
    if (!didRestoreRef.current) return;
    const urlCampaignId = searchParams.get(PARAM.CAMPAIGN_ID);
    const urlCampaignName = searchParams.get(PARAM.CAMPAIGN_NAME);
    const urlAdGroupId = searchParams.get(PARAM.ADGROUP_ID);
    const urlAdGroupName = searchParams.get(PARAM.ADGROUP_NAME);
    const urlKeywordId = searchParams.get(PARAM.KEYWORD_ID);
    const urlKeywordName = searchParams.get(PARAM.KEYWORD_NAME);
    const campaignOption =
      urlCampaignId && urlCampaignName
        ? (campaignOptions.find((o) => o.value === urlCampaignId) ?? {
            label: urlCampaignName,
            value: urlCampaignId,
          })
        : null;
    const adGroupOption =
      urlAdGroupId && urlAdGroupName
        ? (adGroupOptions.find((o) => o.value === urlAdGroupId) ?? {
            label: urlAdGroupName,
            value: urlAdGroupId,
          })
        : null;
    const keywordOption =
      urlKeywordId && urlKeywordName
        ? (keywordOptions.find((o) => o.value === urlKeywordId) ?? {
            label: urlKeywordName,
            value: urlKeywordId,
          })
        : null;
    dispatch(
      setMobileFiltersPartial({
        campaign: campaignOption,
        adGroup: adGroupOption,
        keyword: keywordOption,
      }),
    );
    if (pendingAutoFetchRef.current) {
      fetchTrends(pendingAutoFetchRef.current);
      pendingAutoFetchRef.current = null;
    }
    // eslint-disable-next-line
  }, [isAllFiltersReady]);

  const handleTrafficSourceChange = async (option: Option | null) => {
    dispatch(
      setMobileFiltersPartial({
        trafficSource: option,
        campaign: null,
        adGroup: null,
        keyword: null,
      }),
    );
    if (!option) return;
    await fetchDependentFilters(option.value);
  };

  const buildParams = () => {
    if (!filters.trafficSource) return null;
    return {
      oauthTokenHeader,
      queryId: TrendsDataSetQueryIds.CLICK,
      startDate: startDate || "",
      endDate: endDate || "",
      timeUnit: "day",
      type: "site",
      matchtype: "all",
      websiteSharedUids: websiteSharedUids ? websiteSharedUids.split(",") : [],
      dimensions: selectedDimensions,
      trafficSource: filters.trafficSource.value,
      country: filters.country?.value || "all",
      campaignId: filters.campaign?.value || "1",
      adgroupId: filters.adGroup?.value || "1",
      kwId: filters.keyword?.value || "1",
    };
  };

  const handleApply = async () => {
    const params = buildParams();
    if (!params) {
      console.error("Traffic source is required");
      return;
    }
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev.toString());
      updated.set(PARAM.TRAFFIC_SOURCE, filters.trafficSource.value);
      if (filters.country) {
        updated.set(PARAM.COUNTRY, filters.country.value);
      } else {
        updated.delete(PARAM.COUNTRY);
      }
      if (filters.campaign) {
        updated.set(PARAM.CAMPAIGN_ID, filters.campaign.value);
        updated.set(PARAM.CAMPAIGN_NAME, filters.campaign.label);
      } else {
        updated.delete(PARAM.CAMPAIGN_ID);
        updated.delete(PARAM.CAMPAIGN_NAME);
      }
      if (filters.adGroup) {
        updated.set(PARAM.ADGROUP_ID, filters.adGroup.value);
        updated.set(PARAM.ADGROUP_NAME, filters.adGroup.label);
      } else {
        updated.delete(PARAM.ADGROUP_ID);
        updated.delete(PARAM.ADGROUP_NAME);
      }
      if (filters.keyword) {
        updated.set(PARAM.KEYWORD_ID, filters.keyword.value);
        updated.set(PARAM.KEYWORD_NAME, filters.keyword.label);
      } else {
        updated.delete(PARAM.KEYWORD_ID);
        updated.delete(PARAM.KEYWORD_NAME);
      }
      return updated;
    });
    toggleDrawer(false)();
    await fetchTrends(params);
  };

  const hasActiveFilters = React.useMemo(() => {
    const mobileHasValue = Object.values(mobileFilters || {}).some(
      (val) => val !== null && val !== undefined,
    );
    const hasNoteLabel = !!notesLabelFilter;
    return mobileHasValue || hasNoteLabel;
  }, [mobileFilters, notesLabelFilter]);

  const sharedSelectSx = {
    borderRadius: 2,
    height: spacing.Spacing["4xl"].$value,
    minWidth: 184,
    maxWidth: 184,
    py: spacing.Spacing.sm.$value,
    fontSize: typography.Size.Text.sm.$value,
    lineHeight: typography.Line_height.text.sm.$value,
    fontWeight: typography.Font_weight.Semibold.$value,
    color: colors.GrayLightMode[700].$value,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.GrayLightMode[300].$value,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.Brand[600].$value,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.Brand[600].$value,
    },
    "& .MuiSelect-icon": { color: colors.GrayLightMode[700].$value },
    "&.Mui-disabled": {
      background: "#E9EAEB",
      color: colors.GrayLightMode[500].$value,
    },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.GrayLightMode[300].$value,
    },
  };

  const labelTypoSx = {
    fontSize: typography.Size.Text.sm.$value,
    lineHeight: typography.Line_height.text.sm.$value,
  };

  const ShortList = (
    <Box>
      <Puller />
      <Stack
        justifyContent="space-between"
        direction="row"
        mt={1.5}
        mb={1}
        alignItems="center"
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: typography.Size.Text.xs.$value,
            lineHeight: typography.Line_height.text.xs.$value,
            color: colors.GrayLightMode[700].$value,
          }}
        >
          Filter by
        </Typography>
        <Typography
          onClick={handleClearAll}
          data-tracker="710"
          sx={{
            fontSize: typography.Size.Text.xs.$value,
            lineHeight: typography.Line_height.text.xs.$value,
            color: colors.GrayLightMode[700].$value,
            fontWeight: typography.Font_weight.Semibold.$value,
            textTransform: "capitalize",
            letterSpacing: 0,
            p: 0,
            cursor: "pointer",
          }}
        >
          Clear All
        </Typography>
      </Stack>

      <Box sx={{ my: spacing.Spacing.lg.$value }}>
        <Stack
          alignItems="flex-start"
          justifyContent="space-between"
          flexDirection="row"
        >
          <Typography
            color={colors.GrayLightMode[700].$value}
            sx={{ ...labelTypoSx, mt: "5px" }}
          >
            Note Labels
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <FormControl size="small">
              <Select
                displayEmpty
                value={notesLabelFilter ?? ""}
                onChange={(e) =>
                  dispatch(setNotesLabelFilter(e.target.value || null))
                }
                onOpen={() => trackOpen("703")}
                endAdornment={
                  notesLabelFilter ? (
                    <IconButton
                      size="small"
                      onClick={() => dispatch(setNotesLabelFilter(null))}
                      sx={{
                        mr: 1.5, // pushes it left of the dropdown arrow
                        p: "2px",
                        color: colors.GrayLightMode[500].$value,
                        "&:hover": {
                          color: colors.GrayLightMode[700].$value,
                          background: "transparent",
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  ) : null
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: typography.Size.Text.sm.$value,
                        minHeight: spacing.Spacing["5xl"].$value,
                      },
                    },
                  },
                }}
                sx={sharedSelectSx}
              >
                {availableLabels.length === 0 ? (
                  <MenuItem value="" disabled>
                    No notes available
                  </MenuItem>
                ) : (
                  <MenuItem value="">Select Notes Label</MenuItem>
                )}
                {availableLabels.map((label) => (
                  <MenuItem key={label} value={label} data-tracker="704">
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ minHeight: "18px", mt: "4px" }}>
              {notesLabelFilter && (
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.xxs.$value,
                    lineHeight: typography.Line_height.text.xxs.$value,
                    color: colors.Brand[600].$value,
                    fontWeight: typography.Font_weight.Medium.$value,
                    mt: "4px",
                  }}
                >
                  Notes filtered by "{notesLabelFilter}"
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>

        <Divider
          sx={{
            my: spacing.Spacing.sm.$value,
            borderWidth: spacing.Spacing.xxs.$value,
            borderColor: colors.GrayLightMode[100].$value,
            borderRadius: spacing.Radius.xxs.$value,
          }}
        />

        {dataSet === "click_date" ? (
          <>
            {!filters.trafficSource && (
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "#6b6b6b",
                  background:
                    "linear-gradient(90deg, #5a5a5aff 25%, #bebdbdff 50%, #5a5a5aff 75%)",
                  backgroundSize: "300% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "shimmer 8s linear infinite",
                  paddingBottom: "10px",
                }}
              >
                Select traffic source to enable "Apply" and other filters.
              </Typography>
            )}
            <Stack
              alignItems="center"
              justifyContent="space-between"
              flexDirection="row"
            >
              <Typography
                color={colors.GrayLightMode[700].$value}
                sx={labelTypoSx}
              >
                Traffic Source
              </Typography>
              <FormControl size="small">
                <VirtualizedAutocomplete
                  options={trafficSourceOptions}
                  value={filters.trafficSource}
                  onChange={handleTrafficSourceChange}
                  placeholder="Select Traffic Source"
                  onOpen={() => trackOpen("705")}
                />
              </FormControl>
            </Stack>
            <Stack
              alignItems="center"
              justifyContent="space-between"
              flexDirection="row"
              sx={{ my: spacing.Spacing.lg.$value }}
            >
              <Typography
                color={colors.GrayLightMode[700].$value}
                sx={labelTypoSx}
              >
                Country
              </Typography>
              <VirtualizedAutocomplete
                options={countryOptions}
                value={filters.country}
                onChange={(val) =>
                  dispatch(updateMobileFilter({ key: "country", value: val }))
                }
                placeholder="Select Country"
                disabled={!isAllFiltersReady || filterLoading}
                loading={filterLoading}
                onOpen={() => trackOpen("706")}
              />
            </Stack>
            <Stack
              alignItems="center"
              justifyContent="space-between"
              flexDirection="row"
              sx={{ my: spacing.Spacing.lg.$value }}
            >
              <Typography
                color={colors.GrayLightMode[700].$value}
                sx={labelTypoSx}
              >
                Campaign
              </Typography>
              <VirtualizedAutocomplete
                options={campaignOptions}
                value={filters.campaign}
                onChange={(val) =>
                  dispatch(updateMobileFilter({ key: "campaign", value: val }))
                }
                placeholder="Select Campaign"
                disabled={!isAllFiltersReady || filterLoading}
                loading={filterLoading}
                onOpen={() => trackOpen("707")}
              />
            </Stack>
            <Stack
              alignItems="center"
              justifyContent="space-between"
              flexDirection="row"
              sx={{ my: spacing.Spacing.lg.$value }}
            >
              <Typography
                color={colors.GrayLightMode[700].$value}
                sx={labelTypoSx}
              >
                Ad Group
              </Typography>
              <VirtualizedAutocomplete
                options={adGroupOptions}
                value={filters.adGroup}
                onChange={(val) =>
                  dispatch(updateMobileFilter({ key: "adGroup", value: val }))
                }
                placeholder="Select Ad Group"
                disabled={!isAllFiltersReady || filterLoading}
                loading={filterLoading}
                onOpen={() => trackOpen("708")}
              />
            </Stack>
            <Stack
              alignItems="center"
              justifyContent="space-between"
              flexDirection="row"
              sx={{ my: spacing.Spacing.lg.$value }}
            >
              <Typography
                color={colors.GrayLightMode[700].$value}
                sx={labelTypoSx}
              >
                Keyword
              </Typography>
              <VirtualizedAutocomplete
                options={keywordOptions}
                value={filters.keyword}
                onChange={(val) =>
                  dispatch(updateMobileFilter({ key: "keyword", value: val }))
                }
                placeholder="Select Keyword"
                disabled={!isAllFiltersReady || filterLoading}
                loading={filterLoading}
                onOpen={() => trackOpen("709")}
              />
            </Stack>
          </>
        ) : null}
      </Box>
      {dateType === "action_date" ? null : (
        <Button
          fullWidth
          variant="contained"
          data-tracker="714"
          disabled={isApplyDisabled}
          onClick={handleApply}
          sx={{
            textTransform: "capitalize",
            boxShadow: "none !important",
            background: colors.Brand[600].$value,
            borderRadius: 2,
            mt: spacing.Spacing.lg.$value,
          }}
        >
          Apply
        </Button>
      )}
    </Box>
  );

  return (
    <>
      <IconButton
        color="secondary"
        size="large"
        onClick={toggleDrawer(true)}
        disabled={loadingTrendsMobData}
        data-tracker="702"
      >
        <Badge
          color="secondary"
          sx={{
            "& .MuiBadge-badge": {
              height: spacing.Spacing.lg.$value,
              width: spacing.Spacing.lg.$value,
              borderRadius: spacing.Radius.full.$value,
              backgroundColor: colors.Brand[600].$value,
            },
          }}
          variant="dot"
          invisible={!hasActiveFilters}
        >
          <TuneIcon sx={{ color: colors.GrayLightMode[700].$value }} />
        </Badge>
      </IconButton>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: spacing.Spacing.xl.$value + "!important",
          },
        }}
      >
        {ShortList}
      </Drawer>
    </>
  );
}
